import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { format } from 'date-fns';
import UserDialog from './UserDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import * as adminService from '../services/adminService';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user info
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleRestoreUser = (user) => {
    setSelectedUser(user);
    setIsRestoreDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleRestoreDialogClose = () => {
    setIsRestoreDialogOpen(false);
  };

  const handleUserSubmit = async (userData) => {
    try {
      setLoading(true);
      
      if (selectedUser) {
        // Update existing user
        await adminService.updateUser(selectedUser.id, userData);
        setSuccess('User updated successfully');
      } else {
        // Create new user
        await adminService.createUser(userData);
        setSuccess('User created successfully');
      }
      
      // Refresh user list
      await fetchUsers();
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error saving user:', err);
      let message = 'Failed to save user.';
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await adminService.deleteUser(selectedUser.id);
      setSuccess('User deactivated successfully');
      await fetchUsers();
      setIsDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting user:', err);
      let message = 'Failed to deactivate user.';
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreConfirm = async () => {
    try {
      setLoading(true);
      await adminService.restoreUser(selectedUser.id);
      setSuccess('User restored successfully');
      await fetchUsers();
      setIsRestoreDialogOpen(false);
    } catch (err) {
      console.error('Error restoring user:', err);
      let message = 'Failed to restore user.';
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSuccess(null);
    setError(null);
  };

  return (
    <Card>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Typography variant="h5" component="h2">
            User Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </div>
        
        {loading && <Typography>Loading users...</Typography>}
        
        {!loading && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No users found</TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Chip 
                            icon={<AdminPanelSettingsIcon />} 
                            label="Administrator"
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Chip 
                            icon={<PersonIcon />} 
                            label="Standard User"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          color="primary"
                          onClick={() => handleEditUser(user)}
                          disabled={!currentUser?.is_admin}
                        >
                          <EditIcon />
                        </IconButton>
                        
                        {user.is_active ? (
                          <IconButton 
                            color="error"
                            onClick={() => handleDeleteUser(user)}
                            disabled={!currentUser?.is_admin || currentUser?.id === user.id}
                            title={currentUser?.id === user.id ? "You cannot deactivate your own account" : "Deactivate user"}
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          <IconButton 
                            color="success"
                            onClick={() => handleRestoreUser(user)}
                            disabled={!currentUser?.is_admin}
                            title="Restore user"
                          >
                            <RestoreIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      
      {isDialogOpen && (
        <UserDialog
          open={isDialogOpen}
          user={selectedUser}
          onClose={handleDialogClose}
          onSubmit={handleUserSubmit}
          currentUser={currentUser}
        />
      )}
      
      {isDeleteDialogOpen && (
        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          title="Deactivate User"
          content={`Are you sure you want to deactivate user "${selectedUser?.username}"? This will prevent them from logging in.`}
          onClose={handleDeleteDialogClose}
          onConfirm={handleDeleteConfirm}
        />
      )}
      
      {isRestoreDialogOpen && (
        <DeleteConfirmationDialog
          open={isRestoreDialogOpen}
          title="Restore User"
          content={`Are you sure you want to restore user "${selectedUser?.username}"? This will allow them to log in again.`}
          onClose={handleRestoreDialogClose}
          onConfirm={handleRestoreConfirm}
          confirmButtonText="Restore"
          confirmButtonColor="success"
        />
      )}
      
      <Snackbar open={!!success || !!error} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert 
          onClose={handleSnackbarClose} 
          severity={success ? "success" : "error"} 
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default UserManager;